'use client';

import { useState, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { X } from 'lucide-react';
import { useStaggeredAnimateIn } from '@/lib/useAnimateIn';
import { trackEvent } from '@/lib/analytics';

interface NavigationContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ContactRow {
  email: string;
  subtitle: string;
}

const CONTACT_ROWS: ContactRow[] = [
  {
    email: 'hello@havascx-us.com',
    subtitle: 'General'
  },
  {
    email: 'careers@havascx-us.com',
    subtitle: 'Careers'
  },
  {
    email: '200 Hudson St. New York, NY 10013',
    subtitle: 'U.S. Headquarters'
  },
  {
    email: 'LinkedIn',
    subtitle: 'Follow Us'
  }
];

export default function NavigationContactModal({ isOpen, onClose }: NavigationContactModalProps) {
  const pathname = usePathname();
  const [modalVisible, setModalVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [hoveredLinkIndex, setHoveredLinkIndex] = useState<number | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const backdropRef = useRef<HTMLDivElement | null>(null);
  const { getItemStyle, trigger: triggerItems, reset: resetItems } = useStaggeredAnimateIn({
    delay: 200,
    staggerDelay: 80,
    translateDistance: 20,
    duration: 400,
    autoStart: false
  });

  useEffect(() => {
    if (isOpen) {
      // Set shouldRender immediately so component is in DOM
      setShouldRender(true);
      
      // Prevent background scrolling
      const originalBodyOverflow = document.body.style.overflow;
      const originalHtmlOverflow = document.documentElement.style.overflow;
      
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';

      setModalVisible(false);
      resetItems();
      // Set transform origin to center of screen
      if (modalRef.current) {
        modalRef.current.style.transformOrigin = '50% 50%';
      }
      let timer2: NodeJS.Timeout;
      const timer1 = setTimeout(() => {
        setModalVisible(true);
        // Trigger staggered animations after modal opens
        timer2 = setTimeout(() => {
          triggerItems();
        }, 100);
      }, 50);

      // Add passive: false event listeners to backdrop and modal after a small delay to ensure refs are set
      const handleWheel = (e: WheelEvent) => {
        const target = e.target as HTMLElement;
        const modalEl = modalRef.current;
        
        // If scrolling on backdrop, always prevent
        if (target === backdropRef.current || !modalEl?.contains(target)) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        
        // Find the nearest scrollable element by traversing up the DOM tree
        let element: HTMLElement | null = target;
        while (element && element !== modalEl) {
          const style = window.getComputedStyle(element);
          const isScrollable = 
            element.scrollHeight > element.clientHeight &&
            (style.overflowY === 'auto' || style.overflowY === 'scroll' || 
             element.tagName === 'TEXTAREA');
          
          if (isScrollable) {
            const isScrollingUp = e.deltaY < 0;
            const isScrollingDown = e.deltaY > 0;
            const isAtTop = element.scrollTop <= 0;
            const isAtBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 1;
            
            // If we can scroll in this direction, allow it and stop propagation to body
            if ((isScrollingUp && !isAtTop) || (isScrollingDown && !isAtBottom)) {
              e.stopPropagation();
              return;
            }
            // If at boundary, still stop propagation to prevent body scroll
            e.stopPropagation();
            return;
          }
          
          element = element.parentElement;
        }
        
        // If no scrollable element found, prevent background scroll
        e.preventDefault();
        e.stopPropagation();
      };
      
      const handleTouchMove = (e: TouchEvent) => {
        const target = e.target as HTMLElement;
        const modalEl = modalRef.current;
        
        // If touching backdrop, always prevent
        if (target === backdropRef.current || !modalEl?.contains(target)) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        
        // Allow touch scrolling within scrollable elements
        let element: HTMLElement | null = target;
        while (element && element !== modalEl) {
          const style = window.getComputedStyle(element);
          const isScrollable = 
            element.scrollHeight > element.clientHeight &&
            (style.overflowY === 'auto' || style.overflowY === 'scroll' || 
             element.tagName === 'TEXTAREA');
          
          if (isScrollable) {
            // Allow the scroll to happen
            e.stopPropagation();
            return;
          }
          
          element = element.parentElement;
        }
        
        // If no scrollable element found, prevent background scroll
        e.preventDefault();
        e.stopPropagation();
      };

      const addListeners = () => {
        const backdropEl = backdropRef.current;
        const modalEl = modalRef.current;
        if (backdropEl) {
          backdropEl.addEventListener('wheel', handleWheel, { passive: false });
          backdropEl.addEventListener('touchmove', handleTouchMove, { passive: false });
        }
        if (modalEl) {
          modalEl.addEventListener('wheel', handleWheel, { passive: false });
          modalEl.addEventListener('touchmove', handleTouchMove, { passive: false });
        }
      };

      // Add listeners after a small delay to ensure refs are available
      const listenerTimer = setTimeout(addListeners, 10);

      return () => {
        clearTimeout(timer1);
        clearTimeout(listenerTimer);
        if (timer2) clearTimeout(timer2);
        // Restore scrolling
        document.body.style.overflow = originalBodyOverflow;
        document.documentElement.style.overflow = originalHtmlOverflow;
        // Remove event listeners
        const backdropEl = backdropRef.current;
        const modalEl = modalRef.current;
        if (backdropEl) {
          backdropEl.removeEventListener('wheel', handleWheel);
          backdropEl.removeEventListener('touchmove', handleTouchMove);
        }
        if (modalEl) {
          modalEl.removeEventListener('wheel', handleWheel);
          modalEl.removeEventListener('touchmove', handleTouchMove);
        }
      };
    } else {
      // Animate out when closing
      if (shouldRender) {
        setModalVisible(false);
        resetItems();
        // Wait for animation to complete before removing from DOM
        const closeTimer = setTimeout(() => {
          setShouldRender(false);
        }, 300); // Match the faster close transition duration
        return () => clearTimeout(closeTimer);
      }
    }
  }, [isOpen]);

  // Keep in DOM during close animation
  // Only render if shouldRender is true
  if (!shouldRender) return null;

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center">
      <div 
        ref={backdropRef}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity ease-out"
        style={{ 
          opacity: modalVisible ? 1 : 0,
          transitionDuration: modalVisible ? '500ms' : '300ms'
        }}
        role="button"
        tabIndex={0}
        aria-label="Close contact dialog"
        onClick={onClose}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClose(); } }}
      />
      
      <div 
        ref={modalRef}
        className="relative bg-black text-white rounded-3xl border border-gray-900 w-full max-w-5xl mx-4 h-[90dvh] max-h-[90dvh] lg:h-[90vh] lg:max-h-[90vh] overflow-hidden flex flex-col" 
        role="dialog" 
        aria-modal="true" 
        aria-labelledby="contact-dialog-title"
        style={{
          opacity: modalVisible ? 1 : 0,
          transform: modalVisible ? 'scale(1)' : 'scale(0)',
          transitionDuration: modalVisible ? '400ms' : '300ms',
          transitionProperty: modalVisible ? 'opacity, transform' : 'opacity',
          transitionTimingFunction: modalVisible ? 'cubic-bezier(0.16, 1, 0.3, 1)' : 'ease-out'
        }}
      >
        <button
          type="button"
          onClick={onClose}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClose(); } }}
          aria-label="Close contact dialog"
          className="focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none inline-flex items-center justify-center rounded-md hover:bg-[#FDFEF6]/10 absolute top-4 lg:top-[2rem] right-4 lg:right-[2rem] z-10"
          style={getItemStyle(0)}
        >
          <X size={32} className="text-white lg:w-10 lg:h-10 w-8 h-8" aria-hidden="true" />
        </button>
        <div className="flex-1 min-h-0 px-[1.5625rem] lg:px-[5.625rem] py-10">
          <div className="h-full overflow-y-auto flex flex-col">
            <div className="flex-1 flex flex-col justify-center">
              <h1 id="contact-dialog-title" className="heading-3 mb-[3.4375rem]" style={getItemStyle(0)}>What&rsquo;s on your mind?</h1>
              <div className="space-y-6">
              {CONTACT_ROWS.map((row, index) => {
                const isHovered = hoveredLinkIndex === index;
                const isLinkedIn = row.email === 'LinkedIn';
                const isAddress = row.subtitle === 'U.S. Headquarters';
                const googleMapsUrl = 'https://www.google.com/maps/place/Havas/@40.7229489,-74.0103957,17z/data=!3m1!5s0x89c259f4bec74c0f:0x1f4d1a47344f8d3!4m14!1m7!3m6!1s0x89c259f2b887fe81:0x63cf9abc66b5d73!2sHavas!8m2!3d40.7229449!4d-74.0078208!16s%2Fg%2F1pv09bbm4!3m5!1s0x89c259f2b887fe81:0x63cf9abc66b5d73!8m2!3d40.7229449!4d-74.0078208!16s%2Fg%2F1pv09bbm4?entry=ttu&g_ep=EgoyMDI1MTIwMi4wIKXMDSoASAFQAw%3D%3D';
                
                // Determine href based on row type
                let href: string | undefined;
                if (row.email.includes('@')) {
                  href = `mailto:${row.email}`;
                } else if (isLinkedIn) {
                  href = 'https://www.linkedin.com/company/havas-cx';
                } else if (isAddress) {
                  href = googleMapsUrl;
                }
                
                return (
                  <div key={index} className={isLinkedIn ? "" : "pb-4"} style={getItemStyle(index + 1)}>
                    <p className="eyebrow-sm">
                      {row.subtitle}
                    </p>
                    <a 
                      href={href}
                      className="body-lg-lg-xl block mb-1 relative max-w-fit transition-colors duration-300 no-underline"
                      style={{
                        textDecoration: 'none'
                      }}
                      target={!row.email.includes('@') ? '_blank' : undefined}
                      rel={!row.email.includes('@') ? 'noopener noreferrer' : undefined}
                      onMouseEnter={() => {
                        setHoveredLinkIndex(index);
                      }}
                      onMouseLeave={() => {
                        setHoveredLinkIndex(null);
                      }}
                      onClick={() => {
                        // Track click for all link types
                        if (href) {
                          trackEvent('get_in_touch_click', {
                            click_text: row.email,
                            click_url: href,
                            page_path: pathname
                          });
                        }
                        // Clear hover state after click
                        setTimeout(() => {
                          setHoveredLinkIndex(null);
                        }, 100);
                      }}
                    >
                      <span 
                        className="absolute bottom-[-2px] left-0 h-[2px] bg-[#ef4444] transition-all duration-300 ease-out"
                        style={{
                          width: isHovered ? '100%' : '0%'
                        }}
                      />
                      {row.email}
                    </a>
                  </div>
                );
              })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
